import { useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import type { CreateIngredientRequest } from '../../../../models/Ingredient';

interface CreateIngredientModalProps {
  readonly show: boolean;
  readonly onHide: () => void;
  readonly onSubmit: (data: CreateIngredientRequest) => void;
  readonly isSubmitting: boolean;
}

export const CreateIngredientModal = ({ show, onHide, onSubmit, isSubmitting }: CreateIngredientModalProps) => {
  const [formData, setFormData] = useState<CreateIngredientRequest>({
    ingredientName: '',
    risk: '',
    engName: '',
    korName: '',
    description: '',
    efficacy: '',
    efficacyDescription: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.ingredientName.trim()) {
      newErrors['ingredientName'] = 'Ingredient name is required';
    }
    if (!formData.risk.trim()) {
      newErrors['risk'] = 'Risk level is required';
    }
    if (!formData.engName.trim()) {
      newErrors['engName'] = 'English name is required';
    }
    if (!formData.korName.trim()) {
      newErrors['korName'] = 'Korean name is required';
    }
    if (!formData.description.trim()) {
      newErrors['description'] = 'Description is required';
    }
    if (!formData.efficacy.trim()) {
      newErrors['efficacy'] = 'Efficacy is required';
    }
    if (!formData.efficacyDescription.trim()) {
      newErrors['efficacyDescription'] = 'Efficacy description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof CreateIngredientRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      ingredientName: '',
      risk: '',
      engName: '',
      korName: '',
      description: '',
      efficacy: '',
      efficacyDescription: '',
    });
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Ingredient</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Ingredient Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.ingredientName}
                  onChange={(e) => handleInputChange('ingredientName', e.target.value)}
                  isInvalid={Boolean(errors['ingredientName'])}
                  placeholder="e.g., Water;Aqua"
                />
                <Form.Control.Feedback type="invalid">{errors['ingredientName']}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Risk Level *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.risk}
                  onChange={(e) => handleInputChange('risk', e.target.value)}
                  isInvalid={Boolean(errors['risk'])}
                  placeholder="e.g., 1, 1-2, 2-3"
                />
                <Form.Control.Feedback type="invalid">{errors['risk']}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>English Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.engName}
                  onChange={(e) => handleInputChange('engName', e.target.value)}
                  isInvalid={Boolean(errors['engName'])}
                  placeholder="e.g., Water;Aqua"
                />
                <Form.Control.Feedback type="invalid">{errors['engName']}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Korean Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.korName}
                  onChange={(e) => handleInputChange('korName', e.target.value)}
                  isInvalid={Boolean(errors['korName'])}
                  placeholder="e.g., 정제수"
                />
                <Form.Control.Feedback type="invalid">{errors['korName']}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              isInvalid={Boolean(errors['description'])}
              placeholder="Brief description of the ingredient"
            />
            <Form.Control.Feedback type="invalid">{errors['description']}</Form.Control.Feedback>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Efficacy *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.efficacy}
                  onChange={(e) => handleInputChange('efficacy', e.target.value)}
                  isInvalid={Boolean(errors['efficacy'])}
                  placeholder="e.g., Solvent, Humectant"
                />
                <Form.Control.Feedback type="invalid">{errors['efficacy']}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Efficacy Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={formData.efficacyDescription}
              onChange={(e) => handleInputChange('efficacyDescription', e.target.value)}
              isInvalid={Boolean(errors['efficacyDescription'])}
              placeholder="Detailed description of the ingredient's efficacy and benefits"
            />
            <Form.Control.Feedback type="invalid">{errors['efficacyDescription']}</Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Ingredient'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
